import { Component, ElementRef, effect, input, output, signal, viewChild } from '@angular/core';

@Component({
  selector: 'app-time-picker',
  standalone: true,
  template: `
    <label class="time-shell">
      @if (label()) {
        <span>{{ label() }}</span>
      }
      <div class="time-control">
        <input
          type="text"
          class="time-text-input"
          inputmode="numeric"
          [required]="required()"
          [value]="displayValue()"
          placeholder="hh:mm"
          (input)="onTextInput($event)"
          (blur)="onTextBlur()"
          (keydown.enter)="onTextEnter($event)"
        />
        <button
          type="button"
          class="time-picker-btn"
          (click)="openNativePicker($event)"
          [attr.aria-label]="label() || 'Selecionar hora'"
        >
          <span aria-hidden="true"></span>
        </button>
        <input
          #nativeTimeInput
          type="time"
          class="time-native-input"
          [required]="required()"
          [value]="nativeValue()"
          (change)="onNativeTimeChange($event)"
        />
      </div>
    </label>
  `,
  styles: [
    `
      .time-shell {
        display: grid;
        gap: 4px;
        width: 100%;
        min-width: 0;
      }

      .time-shell span {
        font-size: 12px;
        color: var(--faint);
      }

      .time-control {
        position: relative;
      }

      .time-text-input {
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
        height: 36px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        color: var(--text);
        padding: 0 10px;
        font-size: 13px;
        padding-right: 38px;
      }

      .time-picker-btn {
        position: absolute;
        top: 50%;
        right: 4px;
        transform: translateY(-50%);
        width: 28px;
        height: 28px;
        border: none;
        border-radius: 8px;
        background: transparent;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .time-picker-btn:hover {
        background: color-mix(in srgb, var(--hover) 65%, transparent);
      }

      .time-picker-btn span {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 2px solid var(--muted);
        border-radius: 50%;
        position: relative;
      }

      .time-picker-btn span::before {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: 2px;
        height: 5px;
        background: var(--muted);
        transform: translate(-50%, -100%);
      }

      .time-picker-btn span::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: 4px;
        height: 2px;
        background: var(--muted);
        transform: translate(-1px, -50%);
      }

      .time-native-input {
        position: absolute;
        top: 0;
        right: 0;
        width: 36px;
        height: 36px;
        opacity: 0;
        cursor: pointer;
        pointer-events: none;
      }
    `,
  ],
})
export class TimePickerComponent {
  readonly label = input<string>('');
  readonly value = input<string | null>(null);
  readonly valueChange = output<string | null>();
  readonly required = input<boolean>(false);
  private readonly nativeTimeInput = viewChild<ElementRef<HTMLInputElement>>('nativeTimeInput');

  protected readonly displayValue = signal('');
  protected readonly nativeValue = signal<string>('');

  constructor() {
    effect(() => {
      const normalized = this.normalizeTime(this.value());
      this.nativeValue.set(normalized ?? '');
      this.displayValue.set(normalized ?? '');
    });
  }

  protected onTextInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.displayValue.set(value);
  }

  protected onTextBlur(): void {
    this.commitTextValue();
  }

  protected onTextEnter(event: Event): void {
    event.preventDefault();
    this.commitTextValue();
  }

  protected onNativeTimeChange(event: Event): void {
    const raw = (event.target as HTMLInputElement | null)?.value?.trim() ?? '';
    const normalized = this.normalizeTime(raw);
    if (!normalized) {
      this.displayValue.set('');
      this.nativeValue.set('');
      this.valueChange.emit(null);
      return;
    }
    this.displayValue.set(normalized);
    this.nativeValue.set(normalized);
    this.valueChange.emit(normalized);
  }

  protected openNativePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const input = this.nativeTimeInput()?.nativeElement;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }
    input.focus();
    input.click();
  }

  private commitTextValue(): void {
    const raw = this.displayValue().trim();
    if (!raw.length) {
      this.nativeValue.set('');
      this.valueChange.emit(null);
      return;
    }
    const normalized = this.normalizeTime(raw);
    if (!normalized) {
      const fallback = this.normalizeTime(this.value());
      this.displayValue.set(fallback ?? '');
      this.nativeValue.set(fallback ?? '');
      this.valueChange.emit(fallback);
      return;
    }
    this.displayValue.set(normalized);
    this.nativeValue.set(normalized);
    this.valueChange.emit(normalized);
  }

  /**
   * Aceita HH:mm ou HH:mm:ss. Retorna HH:mm.
   */
  private normalizeTime(raw: string | null | undefined): string | null {
    const t = (raw ?? '').trim();
    const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(t);
    if (!match) return null;
    const h = Number(match[1]);
    const m = Number(match[2]);
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
}
